import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChannelType } from '../../types';
import {
  commonBackInfo,
  dealwithChannel,
  dealwithChannelToResult,
  highlight,
} from '../../utils';
import { channelSplitFlag, channelTextMap } from '../../constants/channel';
import VirtualScrollCustomSelect from '../../components/CustomSelect/VirtualScrollCustomSelect';
import { getAllKeys, OptionsType } from '../../utils/customSelectUtils';
import {
  useGetCustomSelectButtonElement,
  useSetSelectDefaultValue,
} from '../../hooks/init';
import ChannelLabel, { channelInfo } from '../../../ChannelLabel';
import './index.scss';
import { channelPlaceholder } from '../../constants/placeholder';
import { getLocale, useIntl } from 'umi';
import { transChannel } from '@/pages/opinion/utils';
import { dealwithChannelToCommon } from '../../utils/storage';
import _ from 'lodash-es';
import { channelExceedLimitTips } from '../../../BubbleCommon';
import { Bubble } from 'tea-component';
import useIntersectionObserver from '@/pages/opinion/pages/Summary/hooks/useIntersectionObserver';

const MAX_CHANNEL_LIMIT = 2000;

/**
 * TODO: 其他系统在使用
 * @param param0
 * @returns
 */
export default function SingleChannelSelect({
  changeOnCloseWindow,
  onChange,
  defaultValue,
  defaultKey,
  filterOptions,
  filterValue,
  oriDefaultFilter,
  backOriDefaultValue,
  channelKey,
  lang,
  firstExcludeOptions,
  isSpecialChannel,
  pointKey,
  setConfirmDisable,
}: ChannelType & { setConfirmDisable?: (value: boolean) => void; }) {
  // 搜索的数据
  const [searchValue, setSearchValue] = useState('');
  const { formatMessage } = useIntl();
  const ref = useRef(null);

  const { show } = useIntersectionObserver({
    target: ref.current,
    threshold: 1,
  });
  const [basicOptions, valueMap]: [
    OptionsType,
    { [key: string]: Array<string> },
  ] = useMemo(() => {
    const { options, valueMap } = dealwithChannel(filterOptions);
    if (channelKey) {
      return [options[channelKey], valueMap];
    }
    return [options, valueMap];
  }, [channelKey, filterOptions]);

  const options: OptionsType = useMemo(() => {
    const { options } = dealwithChannel(filterOptions, searchValue);

    if (channelKey) {
      return options[channelKey];
    }
    return options;
  }, [channelKey, filterOptions, searchValue]);

  const [filterValueMemo, key] = useMemo(() => {
    const key = `channel_${channelKey}`;
    if (_.has(filterValue, key)) {
      return [filterValue, key];
    }

    const value = dealwithChannelToCommon(filterValue);

    return [value, key];
  }, [filterValue]);

  const {
    selects: selectChannel,
    setSelects: setSelectChannel,
    diffClassName,
  } = useSetSelectDefaultValue<string>({
    defaultValue,
    filterValue: filterValueMemo,
    oriDefaultFilter,
    backOriDefaultValue,
    defaultKey: key,
    options: getAllKeys(basicOptions),
    mustFilter: false,
    firstExcludeOptions,
    finallyDealwithSelected: (selected) => {
      const allValue = getAllKeys(basicOptions) || [];
      return selected
        .map((item) => {
          if (!item.includes(channelSplitFlag)) {
            return channelKey + channelSplitFlag + item.toLocaleLowerCase();
          }
          if (!item.startsWith(channelKey!)) {
            return '';
          }
          return item.toLocaleLowerCase();
        })
        .filter(
          (item) =>
            item !== '' &&
            item.startsWith(channelKey!) &&
            allValue.includes(item),
        );
    },
  });

  const { customSelectRef, getButtonElement } =
    useGetCustomSelectButtonElement();

  // 检查实际传给后端的数据量是否超出限制（全选不计入，news反选时用排除数）
  const isExceeded = useMemo(() => {
    const allKeys = getAllKeys(basicOptions) || [];
    if (selectChannel.length === 0 || selectChannel.length === allKeys.length) {
      return false;
    }
    let transmitCount = selectChannel.length;
    if (channelKey === 'news' && selectChannel.length > allKeys.length / 2) {
      transmitCount = allKeys.length - selectChannel.length;
    }
    return transmitCount > MAX_CHANNEL_LIMIT;
  }, [selectChannel, basicOptions, channelKey]);

  const showError = isExceeded && show;

  useEffect(() => {
    setConfirmDisable?.(isExceeded);
  }, [isExceeded]);

  const showErrorInfo = useMemo(() => {
    if (!showError) return null;
    const locale = getLocale();
    const key = locale === 'zh-CN' ? 'zh' : 'en';
    return channelExceedLimitTips[key] || null;
  }, [showError, show]);

  function getCommonBackInfo() {
    const [text, isAll] = getButtonElement() || [];

    const { result: channelInfo, deleteKey } = dealwithChannelToResult(
      selectChannel,
      isAll,
      valueMap,
    );

    const { exclude_channels, ...otherInfo } = channelInfo;

    const key = `channel_${channelKey}` as 'channel_social';

    const values = otherInfo?.[key] || ['-99999'];
    const result: { [key: string]: Array<string> | string } = {
      channel: values,
      channel_type: channelKey as any,
    };

    if (exclude_channels) {
      result.exclude_channels = exclude_channels;
    }

    return {
      data: commonBackInfo(
        result,
        { [defaultKey]: text },
        { isAll, needChangeEntity: true },
      ),
      deleteKey,
    };
  }

  useEffect(() => {
    const { data, deleteKey } = getCommonBackInfo();
    onChange?.('channel', data, deleteKey);
  }, [selectChannel, formatMessage]);

  const getName = useCallback((value: any) => {
    value = value?.split(channelSplitFlag)?.[1] || value;
    const { name } = channelInfo(value);
    return [name, value];
  }, []);

  const buttonItemRender = (value: any) => {
    let [name, channelName] = getName(value);
    name = transChannel(name);
    return (
      <div className="dbo-select-channel-item">
        <ChannelLabel
          className="dbo-select-channel-icon"
          channel_name={channelName}
          showText={false}
        ></ChannelLabel>
        <span>{highlight(name, searchValue)}</span>
      </div>
    );
  };

  const optionItemRender = (_element: any, value: any) => {
    let [name, channelName] = getName(value);
    name = transChannel(name);
    return (
      <div className="dbo-select-channel-item">
        <ChannelLabel
          className="dbo-select-channel-icon"
          channel_name={channelName}
          showText={false}
        ></ChannelLabel>
        <span>{highlight(name, searchValue)}</span>
      </div>
    );
  };

  return (
    <div ref={ref} className={`single-channel-select-wrapper ${diffClassName} ${showError ? 'dbo-single-channel-select-wrapper-error' : ''}`}>
      <Bubble
        visible={show}
        error={true} 
        content={showErrorInfo}
      >
        <div>
          <VirtualScrollCustomSelect
            ref={customSelectRef}
            placeholderText={channelPlaceholder[lang]}
            allShowModal="text"
            allLabel={formatMessage({ id: 'Channel' })}
            onClear={() => setSelectChannel([])}
            basicOptions={basicOptions}
            searchInfo={[searchValue, setSearchValue]}
            selectOptionsInfo={[selectChannel, setSelectChannel]}
            subTextMap={channelTextMap(formatMessage)}
            options={options}
            boxClassName="channel-select-wrapper-box"
            buttonItemRender={buttonItemRender}
            optionItemRender={optionItemRender}
            matchButtonWidth={false}
            pointKey={pointKey}
            onChange={() => {
              const data = getCommonBackInfo();

              changeOnCloseWindow?.(data.value);
            }}
          ></VirtualScrollCustomSelect>
        </div>
      </Bubble>
    </div>
  );
}
